import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { SeasonalDesignIndexTable } from '@/components/seasonal/SeasonalDesignIndexTable'
import { SeasonalScheduleList } from '@/components/seasonal/SeasonalScheduleList'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  seasonalContentQueries,
  useSeasonalDesigns,
  useSeasonalSchedules,
} from '@/lib/services/seasonal-content/seasonal-content.query'
import { requireGlobalSeasonalContentAccess } from '@/lib/services/seasonal-content/seasonal-content.route'
import {
  merchandisingTagQueries,
  useMerchandisingTagGroups,
} from '@/lib/services/merchandising-tags/merchandising-tags.query'
import { flattenSeasonalContentTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { createFileRoute, Link, stripSearchParams, useNavigate } from '@tanstack/react-router'
import { CalendarDays, Images } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod/v4'

const DEFAULT_PAGE_SIZE = 20
const defaultSearch = { sort: 'name', page: 1, page_size: DEFAULT_PAGE_SIZE }
const searchSchema = z.object({
  search: z.string().optional().catch(undefined),
  seasonal_tag_id: z.coerce.number().int().positive().optional().catch(undefined),
  sort: z
    .enum([
      'name',
      'name_desc',
      'updated_desc',
      'updated_asc',
      'usage_desc',
      'usage_asc',
      'live_desc',
      'live_asc',
    ])
    .catch(defaultSearch.sort),
  page: z.coerce.number().int().positive().catch(defaultSearch.page),
  page_size: z.coerce.number().int().positive().catch(defaultSearch.page_size),
})

const contentTabs = [
  { value: 'designs', label: 'Seasonal designs', icon: Images },
  { value: 'schedules', label: 'Default schedules', icon: CalendarDays },
]

export const Route = createFileRoute('/_authenticated/$orgId/seasonal-content/')({
  component: SeasonalContentPage,
  head: adminRouteTitleHead,
  validateSearch: searchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        sort: defaultSearch.sort,
        page: defaultSearch.page,
        page_size: defaultSearch.page_size,
      }),
    ],
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, params, deps }) => {
    const access = requireGlobalSeasonalContentAccess({ context, params })

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      seasonalContentQueries.designs({ organizationId: params.orgId, filters: deps.search })
    )
    await context.queryClient.ensureQueryData(
      merchandisingTagQueries.index({ organizationId: params.orgId })
    )

    return access
  },
})

export function SeasonalContentPage() {
  const { isReadOnly } = Route.useLoaderData()
  const { orgId } = Route.useParams()
  const urlSearch = Route.useSearch()
  const path = usePathHelper()
  const navigate = useNavigate()
  const { updateSearchTerm, updateSort, updatePage, updatePageSize, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: (search) => searchSchema.parse(search),
      searchTerm: urlSearch.search ?? '',
    })
  const {
    data: designsResponse,
    isFetching: isFetchingDesigns,
    isPending: isPendingDesigns,
  } = useSeasonalDesigns({ organizationId: orgId, filters: urlSearch })
  const [activeTab, setActiveTab] = useState('designs')
  const { data: schedulesResponse, isPending: isPendingSchedules } = useSeasonalSchedules({
    organizationId: orgId,
    enabled: activeTab === 'schedules',
  })
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({ organizationId: orgId })
  const seasonalTagOptions = flattenSeasonalContentTagGroups(merchandisingTagGroups)
  const schedules = schedulesResponse?.data ?? []
  const clearDesignFilters = () =>
    updateTableParams({ search: undefined, seasonal_tag_id: undefined })

  return (
    <AdminPageShell>
      <AdminPageHeader
        icon={CalendarDays}
        title="Seasonal content"
        description="Create complete seasonal website looks and choose the default timing new florists receive."
        actions={
          !isReadOnly && activeTab === 'designs' ? (
            <CreateActionButton asChild>
              <Link {...path.seasonalContent.create().using({ orgId }).link}>Create design</Link>
            </CreateActionButton>
          ) : null
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="gap-4">
        <TabsList aria-label="Seasonal content sections">
          {contentTabs.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value}>
              <Icon aria-hidden="true" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="designs">
          <SeasonalDesignIndexTable
            response={designsResponse}
            search={urlSearch.search ?? ''}
            occasions={seasonalTagOptions}
            occasionId={urlSearch.seasonal_tag_id}
            sort={urlSearch.sort}
            page={urlSearch.page}
            pageSize={urlSearch.page_size}
            isFetching={isFetchingDesigns}
            isPending={isPendingDesigns}
            onSearchChange={updateSearchTerm}
            onOccasionChange={(seasonalTagId) =>
              updateTableParams({ seasonal_tag_id: seasonalTagId })
            }
            onClearFilters={clearDesignFilters}
            onSortChange={updateSort}
            onPageChange={updatePage}
            onPageSizeChange={updatePageSize}
            isReadOnly={isReadOnly}
            organizationId={orgId}
          />
        </TabsContent>

        <TabsContent value="schedules">
          {isPendingSchedules ? (
            <div className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
              Loading default schedules…
            </div>
          ) : (
            <SeasonalScheduleList
              profiles={schedules}
              merchandisingTagGroups={merchandisingTagGroups}
              isReadOnly={isReadOnly}
              organizationId={orgId}
            />
          )}
        </TabsContent>
      </Tabs>
    </AdminPageShell>
  )
}
